import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React, { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useImageAttachmentMutation } from '../../../queries/Attachments/useImageAttachmentMutation';
import { useGetImageUrl } from '../../components/TestimonialImageEditor/hooks/useGetImageUrl';
import { Button } from '../../components-v2/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '../../components-v2/Dialog';
import { Field, FieldDescription, FieldLabel } from '../../components-v2/Field';
import { ICropFraming, ImageCropDialog } from '../../components-v2/ImageCropDialog';
import { Input } from '../../components-v2/Input';
import { UnifiedImageDialog } from '../../components-v2/UnifiedImageDialog';
import { styleSheet as s } from './styles';

interface Props {
	title: string;
	/** Card and person photos have no alt text, so the description field is left out for them. */
	acceptsAlt: boolean;
	isSaving: boolean;
	onConfirm: (image: Api.IFileAttachment, alt?: string) => void;
	onCancel: () => void;
}

export function PreviewImagePicker({ title, acceptsAlt, isSaving, onConfirm, onCancel }: Props) {
	const altInputId = useId();
	const [image, setImage] = useState<Api.IFileAttachment | null>(null);
	const [alt, setAlt] = useState('');
	// Every crop is cut from the picture first picked, so cropping again never crops a crop.
	const [originalUrl, setOriginalUrl] = useState<string | null>(null);
	const [framing, setFraming] = useState<ICropFraming>();
	const [isCropOpen, setIsCropOpen] = useState(false);
	const [isPreparingCrop, setIsPreparingCrop] = useState(false);
	// The image dialog closes itself in the same tick it reports a pick, before `image` has re-rendered.
	const pickedRef = useRef(false);
	const { getImageUrl } = useGetImageUrl();
	const { mutateAsync: uploadCroppedImage } = useImageAttachmentMutation();

	const handleImageSelected = (selected: Api.IFileAttachment) => {
		pickedRef.current = true;
		setImage(selected);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (image) {
			onConfirm(image, acceptsAlt ? alt.trim() || undefined : undefined);
		}
	};

	const openCrop = async () => {
		if (originalUrl) {
			setIsCropOpen(true);
			return;
		}
		if (!image) {
			return;
		}
		setIsPreparingCrop(true);
		try {
			// The cropper needs a data URL: a cross-origin picture taints the canvas and the crop then fails.
			setOriginalUrl(await getImageUrl(image));
			setIsCropOpen(true);
		} catch {
			toast.error('This picture couldn’t be opened for cropping. Please try again.');
		} finally {
			setIsPreparingCrop(false);
		}
	};

	const handleCropComplete = async (croppedFile: File, cropFraming: ICropFraming) => {
		let cropped: Api.IFileAttachment;
		try {
			[cropped] = await uploadCroppedImage({ files: [croppedFile], addToLibrary: true });
		} catch {
			throw new Error('Could not upload the cropped picture. Please try again.');
		}
		setImage(cropped);
		setFraming(cropFraming);
	};

	return (
		<>
			{/* The API only takes the account's own library images, so every upload is saved there. */}
			<UnifiedImageDialog
				open={!image}
				onOpenChange={open => !open && !pickedRef.current && onCancel()}
				onImageSelected={handleImageSelected}
				addUploadsToLibrary
				showImageLibraryTab
				initialTab='image-library'
			/>
			<Dialog open={Boolean(image) && !isCropOpen} onOpenChange={open => !open && !isSaving && onCancel()}>
				<DialogPopup>
					<form onSubmit={handleSubmit}>
						<DialogHeader>
							<DialogTitle>{title}</DialogTitle>
						</DialogHeader>
						<DialogBody styles={[s.imageConfirmBody]}>
							{image?.url ? <img className={css(s.imageConfirmPreview)} src={image.url} alt='' /> : null}
							{acceptsAlt ? (
								<Field>
									<FieldLabel htmlFor={altInputId}>Describe the picture</FieldLabel>
									<Input
										id={altInputId}
										autoFocus
										value={alt}
										onChange={e => setAlt(e.target.value)}
										placeholder='e.g. A family in front of their new home'
									/>
									<FieldDescription>
										Screen readers read this aloud to visitors who can’t see the picture.
									</FieldDescription>
								</Field>
							) : null}
						</DialogBody>
						<DialogFooter>
							<Button
								type='button'
								variant='secondary'
								onClick={openCrop}
								disabled={isSaving || isPreparingCrop}
								styles={[s.imageConfirmCrop]}
							>
								{isPreparingCrop ? 'Opening…' : 'Crop'}
							</Button>
							<Button type='button' variant='secondary' onClick={onCancel} disabled={isSaving}>
								Cancel
							</Button>
							<Button type='submit' disabled={isSaving || isPreparingCrop}>
								{isSaving ? 'Saving…' : 'Use this picture'}
							</Button>
						</DialogFooter>
					</form>
				</DialogPopup>
			</Dialog>
			{originalUrl && image ? (
				<ImageCropDialog
					open={isCropOpen}
					onOpenChange={setIsCropOpen}
					title='Crop picture'
					applyLabel='Use this crop'
					fileName={image.fileName}
					imageUrl={originalUrl}
					initialFraming={framing}
					onCropComplete={handleCropComplete}
				/>
			) : null}
		</>
	);
}
